import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { InvalidParamError } from '@dynamic-labs-sdk/client';
import {
  type TransferAmountParams,
  parseAmountToSmallestUnit,
} from '@dynamic-labs-sdk/client/core';
import { isEvmWalletAccount } from '@dynamic-labs-sdk/evm';
import type { Hex } from 'viem';
import { encodeFunctionData } from 'viem';

import { ERC20_TRANSFER_ABI } from '../../constants';
import { createKernelClientForWalletAccount } from '../../createKernelClientForWalletAccount';
import { isGasSponsorshipError } from '../../isGasSponsorshipError';

const EVM_NATIVE_DECIMALS = 18;

/**
 * Transfers native ETH or an ERC-20 token via a ZeroDev smart wallet.
 *
 * Attempts with gas sponsorship first, then falls back to unsponsored
 * if the paymaster rejects.
 */
export const zerodevTransferAmount = async (
  { amount, recipient, token, walletAccount }: TransferAmountParams,
  client: DynamicClient
): Promise<{ transactionHash: string }> => {
  if (!isEvmWalletAccount(walletAccount)) {
    throw new InvalidParamError('Wallet account is not an EVM wallet account');
  }

  const sendTransaction = async (withSponsorship: boolean) => {
    const kernelClient = await createKernelClientForWalletAccount(
      { smartWalletAccount: walletAccount, withSponsorship },
      client
    );

    if (token?.address) {
      const smallestUnit = parseAmountToSmallestUnit({
        amount,
        decimals: token.decimals,
      });

      const data = encodeFunctionData({
        abi: ERC20_TRANSFER_ABI,
        args: [recipient as Hex, smallestUnit],
        functionName: 'transfer',
      });

      return kernelClient.sendTransaction({
        data,
        to: token.address as Hex,
      });
    }

    const value = parseAmountToSmallestUnit({
      amount,
      decimals: EVM_NATIVE_DECIMALS,
    });

    return kernelClient.sendTransaction({
      to: recipient as Hex,
      value,
    });
  };

  try {
    const hash = await sendTransaction(true);

    return { transactionHash: hash };
  } catch (error) {
    if (!isGasSponsorshipError(error)) {
      throw error;
    }

    const hash = await sendTransaction(false);

    return { transactionHash: hash };
  }
};
